import React, { useState, useEffect, forwardRef, useImperativeHandle, useMemo } from 'react';
import { Modal, Form, Button, Row, Col, Badge, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPlus, faTrash, faEye } from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import SelectedMetersModal from './SelectedMetersModal';
import { useUsers } from '../hooks/useUsers';
import { createAlertRule, updateAlertRule } from '../services/alertRuleService';
import '../styles/AlertRules.css';


const AlertRuleForm = forwardRef(({ onSave, meters, sources, currentFinancialYear }, ref) => {
  const [show, setShow] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSelectedMeters, setShowSelectedMeters] = useState(false);

  const { data: users = [] } = useUsers();

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    alertType: 'INACTIVITY',
    sourceIds: [],
    subLocationIds: [],
    module: '',
    categories: [],
    subCategories: [],
    kpis: [],
    meterIds: [],
    isEnabled: true,
    escalationPolicy: [
      { notifyAfterMinutes: 15, emails: [], cc: [] }
    ]
  });

  const [validation, setValidation] = useState({});

  // User options for email selection
  const userOptions = useMemo(() => 
    users.map(user => ({
      value: user.email,
      label: `${user.first_name} ${user.last_name}`.trim() || user.email
    })),
    [users]
  );

  // Get available options based on selections
  const availableSubLocations = useMemo(() => {
    if (formData.sourceIds.length === 0) return [];
    
    const selectedSources = sources.filter(s => formData.sourceIds.includes(s.value));
    const subLocs = selectedSources.flatMap(s => s.subLocations || []);
    
    // Remove duplicates
    const uniqueSubLocs = Array.from(
      new Map(subLocs.map(sl => [sl.value, sl])).values()
    );
    
    return uniqueSubLocs;
  }, [formData.sourceIds, sources]);

  const availableModules = useMemo(() => {
    let filteredMeters = meters;

    // Filter by location if selected
    if (formData.sourceIds.length > 0) {
      filteredMeters = filteredMeters.filter(m => formData.sourceIds.includes(m.sourceId));
    }

    // Filter by sub-location if selected
    if (formData.subLocationIds.length > 0) {
      filteredMeters = filteredMeters.filter(m => 
        formData.subLocationIds.includes(m.subLocationId)
      );
    }

    const modules = filteredMeters
      .map(m => m.financialYearKpis?.find(fykpi => fykpi.financialYear === currentFinancialYear)?.kpi?.module)
      .filter(Boolean);

    return [...new Set(modules)].map(m => ({ value: m, label: m }));
  }, [formData.sourceIds, formData.subLocationIds, meters, currentFinancialYear]);

  const availableCategories = useMemo(() => {
    let filteredMeters = meters;

    // Filter by location if selected
    if (formData.sourceIds.length > 0) {
      filteredMeters = filteredMeters.filter(m => formData.sourceIds.includes(m.sourceId));
    }

    // Filter by sub-location if selected
    if (formData.subLocationIds.length > 0) {
      filteredMeters = filteredMeters.filter(m => 
        formData.subLocationIds.includes(m.subLocationId)
      );
    }

    // Filter by module if selected
    if (formData.module) {
      filteredMeters = filteredMeters.filter(m => {
        const kpi = m.financialYearKpis?.find(fykpi => fykpi.financialYear === currentFinancialYear)?.kpi;
        return kpi?.module === formData.module;
      });
    }

    const categories = filteredMeters
      .map(m => m.financialYearKpis?.find(fykpi => fykpi.financialYear === currentFinancialYear)?.kpi?.category)
      .filter(Boolean);

    return [...new Set(categories)].map(c => ({ value: c, label: c }));
  }, [formData.sourceIds, formData.subLocationIds, formData.module, meters, currentFinancialYear]);

  const availableSubCategories = useMemo(() => {
    let filteredMeters = meters;

    // Filter by location if selected
    if (formData.sourceIds.length > 0) {
      filteredMeters = filteredMeters.filter(m => formData.sourceIds.includes(m.sourceId));
    }

    // Filter by sub-location if selected
    if (formData.subLocationIds.length > 0) {
      filteredMeters = filteredMeters.filter(m => 
        formData.subLocationIds.includes(m.subLocationId)
      );
    }

    // Filter by module if selected
    if (formData.module) {
      filteredMeters = filteredMeters.filter(m => {
        const kpi = m.financialYearKpis?.find(fykpi => fykpi.financialYear === currentFinancialYear)?.kpi;
        return kpi?.module === formData.module;
      });
    }

    // Filter by categories if selected
    if (formData.categories.length > 0) {
      filteredMeters = filteredMeters.filter(m => {
        const kpi = m.financialYearKpis?.find(fykpi => fykpi.financialYear === currentFinancialYear)?.kpi;
        return formData.categories.includes(kpi?.category);
      });
    }

    const subCategories = filteredMeters
      .map(m => m.financialYearKpis?.find(fykpi => fykpi.financialYear === currentFinancialYear)?.kpi?.subCategory)
      .filter(Boolean);

    return [...new Set(subCategories)].map(sc => ({ value: sc, label: sc }));
  }, [formData.sourceIds, formData.subLocationIds, formData.module, formData.categories, meters, currentFinancialYear]);

  const availableKpis = useMemo(() => {
    let filteredMeters = meters;

    // Filter by location if selected
    if (formData.sourceIds.length > 0) {
      filteredMeters = filteredMeters.filter(m => formData.sourceIds.includes(m.sourceId));
    }

    // Filter by sub-location if selected
    if (formData.subLocationIds.length > 0) {
      filteredMeters = filteredMeters.filter(m => 
        formData.subLocationIds.includes(m.subLocationId)
      );
    }

    // Filter by module if selected
    if (formData.module) {
      filteredMeters = filteredMeters.filter(m => {
        const kpi = m.financialYearKpis?.find(fykpi => fykpi.financialYear === currentFinancialYear)?.kpi;
        return kpi?.module === formData.module;
      });
    }

    // Filter by categories if selected
    if (formData.categories.length > 0) {
      filteredMeters = filteredMeters.filter(m => {
        const kpi = m.financialYearKpis?.find(fykpi => fykpi.financialYear === currentFinancialYear)?.kpi;
        return formData.categories.includes(kpi?.category);
      });
    }

    // Filter by sub-categories if selected
    if (formData.subCategories.length > 0) {
      filteredMeters = filteredMeters.filter(m => {
        const kpi = m.financialYearKpis?.find(fykpi => fykpi.financialYear === currentFinancialYear)?.kpi;
        return formData.subCategories.includes(kpi?.subCategory);
      });
    }

    const kpis = filteredMeters
      .map(m => m.financialYearKpis?.find(fykpi => fykpi.financialYear === currentFinancialYear)?.kpi?.kpi)
      .filter(Boolean);

    return [...new Set(kpis)].map(k => ({ value: k, label: k }));
  }, [formData.sourceIds, formData.subLocationIds, formData.module, formData.categories, formData.subCategories, meters, currentFinancialYear]);

  const availableMeterIds = useMemo(() => {
    let filteredMeters = meters;

    // Filter by location if selected
    if (formData.sourceIds.length > 0) {
      filteredMeters = filteredMeters.filter(m => formData.sourceIds.includes(m.sourceId));
    }

    // Filter by sub-location if selected
    if (formData.subLocationIds.length > 0) {
      filteredMeters = filteredMeters.filter(m => 
        formData.subLocationIds.includes(m.subLocationId)
      );
    }

    // Filter by module if selected
    if (formData.module) {
      filteredMeters = filteredMeters.filter(m => {
        const kpi = m.financialYearKpis?.find(fykpi => fykpi.financialYear === currentFinancialYear)?.kpi;
        return kpi?.module === formData.module;
      });
    }

    // Filter by categories if selected
    if (formData.categories.length > 0) {
      filteredMeters = filteredMeters.filter(m => {
        const kpi = m.financialYearKpis?.find(fykpi => fykpi.financialYear === currentFinancialYear)?.kpi;
        return formData.categories.includes(kpi?.category);
      });
    }

    // Filter by sub-categories if selected
    if (formData.subCategories.length > 0) {
      filteredMeters = filteredMeters.filter(m => {
        const kpi = m.financialYearKpis?.find(fykpi => fykpi.financialYear === currentFinancialYear)?.kpi;
        return formData.subCategories.includes(kpi?.subCategory);
      });
    }

    // Filter by KPIs if selected
    if (formData.kpis.length > 0) {
      filteredMeters = filteredMeters.filter(m => {
        const kpi = m.financialYearKpis?.find(fykpi => fykpi.financialYear === currentFinancialYear)?.kpi;
        return formData.kpis.includes(kpi?.kpi);
      });
    }

    return filteredMeters.map(m => ({
      value: m.meterId,
      label: m.meterName || m.meterId
    }));
  }, [formData.sourceIds, formData.subLocationIds, formData.module, formData.categories, formData.subCategories, formData.kpis, meters, currentFinancialYear]);

  // Get selected meters for the modal
  const selectedMetersData = useMemo(() => {
    return meters.filter(m => formData.meterIds.includes(m.meterId));
  }, [meters, formData.meterIds]);

  // Auto-select all available meters when filters change
  useEffect(() => {
    // Only auto-select if we're not in edit mode (editingRule is null)
    // and if module is selected (required field)
    if (!editingRule && formData.module && availableMeterIds.length > 0) {
      const allMeterIds = availableMeterIds.map(m => m.value);
      
      // Only update if the meter IDs have changed to avoid infinite loop
      const currentMeterIdsSet = new Set(formData.meterIds);
      const newMeterIdsSet = new Set(allMeterIds);
      
      const hasChanged = 
        currentMeterIdsSet.size !== newMeterIdsSet.size ||
        allMeterIds.some(id => !currentMeterIdsSet.has(id));
      
      if (hasChanged) {
        setFormData(prev => ({ ...prev, meterIds: allMeterIds }));
      }
    }
  }, [availableMeterIds, formData.module, editingRule]);

  useImperativeHandle(ref, () => ({
    show: (rule = null) => {
      if (rule) {
        setEditingRule(rule);
        setFormData({
          name: rule.name || '',
          alertType: rule.alertType || 'INACTIVITY',
          sourceIds: rule.criteria?.sourceId || [],
          subLocationIds: [], // Not stored in criteria, would need to derive
          module: rule.criteria?.module || '',
          categories: rule.criteria?.category || [],
          subCategories: rule.criteria?.subCategory || [],
          kpis: rule.criteria?.kpi || [],
          meterIds: rule.criteria?.meterId || [],
          isEnabled: rule.isEnabled ?? true,
          escalationPolicy: rule.escalationPolicy || [
            { notifyAfterMinutes: 15, emails: [], cc: [] }
          ]
        });
      } else {
        setEditingRule(null);
        setFormData({
          name: '',
          alertType: 'INACTIVITY',
          sourceIds: [],
          subLocationIds: [],
          module: '',
          categories: [],
          subCategories: [],
          kpis: [],
          meterIds: [],
          isEnabled: true,
          escalationPolicy: [
            { notifyAfterMinutes: 15, emails: [], cc: [] }
          ]
        });
      }
      setValidation({});
      setError(null);
      setShow(true);
    }
  }));

  const handleClose = () => {
    setShow(false);
    setEditingRule(null);
    setError(null);
    setValidation({});
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear validation error for this field
    if (validation[field]) {
      setValidation(prev => {
        const newValidation = { ...prev };
        delete newValidation[field];
        return newValidation;
      });
    }

    // Reset downstream fields when parent selection changes
    if (field === 'sourceIds') {
      setFormData(prev => ({
        ...prev,
        sourceIds: value,
        subLocationIds: [],
        module: '',
        categories: [],
        subCategories: [],
        kpis: [],
        meterIds: []
      }));
    } else if (field === 'subLocationIds') {
      setFormData(prev => ({
        ...prev,
        subLocationIds: value,
        module: '',
        categories: [],
        subCategories: [],
        kpis: [],
        meterIds: []
      }));
    } else if (field === 'module') {
      setFormData(prev => ({
        ...prev,
        module: value,
        categories: [],
        subCategories: [],
        kpis: [],
        meterIds: []
      }));
    } else if (field === 'categories') {
      setFormData(prev => ({
        ...prev,
        categories: value,
        subCategories: [],
        kpis: [],
        meterIds: []
      }));
    } else if (field === 'subCategories') {
      setFormData(prev => ({
        ...prev,
        subCategories: value,
        kpis: [],
        meterIds: []
      }));
    } else if (field === 'kpis') {
      setFormData(prev => ({
        ...prev,
        kpis: value,
        meterIds: []
      }));
    }
  };

  const handleEscalationChange = (index, field, value) => {
    const newPolicy = [...formData.escalationPolicy];
    newPolicy[index] = { ...newPolicy[index], [field]: value };
    setFormData(prev => ({ ...prev, escalationPolicy: newPolicy }));
  };

  const addEscalationLevel = () => {
    if (formData.escalationPolicy.length < 3) {
      setFormData(prev => ({
        ...prev,
        escalationPolicy: [
          ...prev.escalationPolicy,
          { notifyAfterMinutes: 30, emails: [], cc: [] }
        ]
      }));
    }
  };

  const removeEscalationLevel = (index) => {
    if (formData.escalationPolicy.length > 1) {
      const newPolicy = formData.escalationPolicy.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, escalationPolicy: newPolicy }));
    }
  };

  // Helper function to validate email format
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Alert name is required';
    }

    if (!formData.module) {
      errors.module = 'Module is required';
    }

    // Location, sublocation, category, subcategory, kpi, and meterid are all optional
    // When not selected, assume all are selected

    // Validate escalation policy
    formData.escalationPolicy.forEach((level, index) => {
      if (!level.notifyAfterMinutes || level.notifyAfterMinutes < 1) {
        errors[`escalation_${index}_minutes`] = `Level ${index + 1}: Notify after minutes must be at least 1`;
      }
      if (level.emails.length === 0) {
        errors[`escalation_${index}_emails`] = `Level ${index + 1}: At least one email is required`;
      } else {
        // Validate email formats
        const invalidEmails = level.emails.filter(email => !isValidEmail(email));
        if (invalidEmails.length > 0) {
          errors[`escalation_${index}_emails`] = `Level ${index + 1}: Invalid email format - ${invalidEmails.join(', ')}`;
        }
      }

      // Validate CC email formats if present
      if (level.cc && level.cc.length > 0) {
        const invalidCcEmails = level.cc.filter(email => !isValidEmail(email));
        if (invalidCcEmails.length > 0) {
          errors[`escalation_${index}_cc`] = `Level ${index + 1}: Invalid CC email format - ${invalidCcEmails.join(', ')}`;
        }
      }
    });

    setValidation(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        userId: 1, // TODO: Get from auth context
        name: formData.name,
        alertType: formData.alertType,
        criteria: {
          sourceId: formData.sourceIds,
          module: formData.module,
          category: formData.categories,
          subCategory: formData.subCategories,
          kpi: formData.kpis,
          meterId: formData.meterIds
        },
        escalationPolicy: formData.escalationPolicy,
        isEnabled: formData.isEnabled
      };

      if (editingRule) {
        await updateAlertRule(editingRule.id, payload);
      } else {
        await createAlertRule(payload);
      }

      handleClose();
      onSave();
    } catch (err) {
      setError(err.message || 'Failed to save alert rule');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingRule ? 'Edit Alert Rule' : 'Add Alert Rule'}
          </Modal.Title>
        </Modal.Header>

        <Form onSubmit={handleSubmit}>
          <Modal.Body className="alert-rule-form-body">
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Basic Information */}
            <div className="form-section mb-4">
              <h6 className="form-section-title">Basic Information</h6>

              <Form.Group className="mb-3">
                <Form.Label>Alert Name *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter alert name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  isInvalid={!!validation.name}
                />
                <Form.Control.Feedback type="invalid">
                  {validation.name}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Alert Type *</Form.Label>
                <Form.Select
                  value={formData.alertType}
                  onChange={(e) => handleChange('alertType', e.target.value)}
                >
                  <option value="INACTIVITY">Inactivity</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  label="Enable Alert Rule"
                  checked={formData.isEnabled}
                  onChange={(e) => handleChange('isEnabled', e.target.checked)}
                />
              </Form.Group>
            </div>

            {/* Criteria Section */}
            <div className="form-section mb-4">
              <h6 className="form-section-title">Alert Criteria</h6>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Location</Form.Label>
                    <Select
                      isMulti
                      options={sources}
                      value={sources.filter(s => formData.sourceIds.includes(s.value))}
                      onChange={(selected) => handleChange('sourceIds', selected.map(s => s.value))}
                      placeholder="All locations (leave blank for all)"
                      className={validation.sourceIds ? 'is-invalid' : ''}
                    />
                    {validation.sourceIds && (
                      <div className="invalid-feedback d-block">{validation.sourceIds}</div>
                    )}
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Sub Location</Form.Label>
                    <Select
                      isMulti
                      options={availableSubLocations}
                      value={availableSubLocations.filter(sl => formData.subLocationIds.includes(sl.value))}
                      onChange={(selected) => handleChange('subLocationIds', selected.map(sl => sl.value))}
                      placeholder="All sub locations (leave blank for all)"
                      isDisabled={availableSubLocations.length === 0}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Module *</Form.Label>
                    <Select
                      options={availableModules}
                      value={availableModules.find(m => m.value === formData.module)}
                      onChange={(selected) => handleChange('module', selected?.value || '')}
                      placeholder="Select module..."
                      isDisabled={availableModules.length === 0}
                      isClearable
                      className={validation.module ? 'is-invalid' : ''}
                    />
                    {validation.module && (
                      <div className="invalid-feedback d-block">{validation.module}</div>
                    )}
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Category</Form.Label>
                    <Select
                      isMulti
                      options={availableCategories}
                      value={availableCategories.filter(c => formData.categories.includes(c.value))}
                      onChange={(selected) => handleChange('categories', selected.map(c => c.value))}
                      placeholder="All categories (leave blank for all)"
                      isDisabled={availableCategories.length === 0}
                      className={validation.categories ? 'is-invalid' : ''}
                    />
                    {validation.categories && (
                      <div className="invalid-feedback d-block">{validation.categories}</div>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Sub Category</Form.Label>
                    <Select
                      isMulti
                      options={availableSubCategories}
                      value={availableSubCategories.filter(sc => formData.subCategories.includes(sc.value))}
                      onChange={(selected) => handleChange('subCategories', selected.map(sc => sc.value))}
                      placeholder="All sub categories (leave blank for all)"
                      isDisabled={availableSubCategories.length === 0}
                      className={validation.subCategories ? 'is-invalid' : ''}
                    />
                    {validation.subCategories && (
                      <div className="invalid-feedback d-block">{validation.subCategories}</div>
                    )}
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>KPI</Form.Label>
                    <Select
                      isMulti
                      options={availableKpis}
                      value={availableKpis.filter(k => formData.kpis.includes(k.value))}
                      onChange={(selected) => handleChange('kpis', selected.map(k => k.value))}
                      placeholder="All KPIs (leave blank for all)"
                      isDisabled={availableKpis.length === 0}
                      className={validation.kpis ? 'is-invalid' : ''}
                    />
                    {validation.kpis && (
                      <div className="invalid-feedback d-block">{validation.kpis}</div>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Meter IDs</Form.Label>
                <Select
                  isMulti
                  options={availableMeterIds}
                  value={availableMeterIds.filter(m => formData.meterIds.includes(m.value))}
                  onChange={(selected) => handleChange('meterIds', selected.map(m => m.value))}
                  placeholder="Auto-selected (all matching meters)"
                  isDisabled={availableMeterIds.length === 0}
                  className={validation.meterIds ? 'is-invalid' : ''}
                />
                {validation.meterIds && (
                  <div className="invalid-feedback d-block">{validation.meterIds}</div>
                )}
                {availableMeterIds.length > 0 && (
                  <Button
                    variant="link"
                    size="sm"
                    className="mt-2 p-0"
                    onClick={() => setShowSelectedMeters(true)}
                  >
                    <FontAwesomeIcon icon={faEye} className="me-1" />
                    Show Selected Meters ({formData.meterIds.length})
                  </Button>
                )}
              </Form.Group>
            </div>

            {/* Escalation Policy Section */}
            <div className="form-section mb-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="form-section-title mb-0">Escalation Policy</h6>
                {formData.escalationPolicy.length < 3 && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={addEscalationLevel}
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-1" />
                    Add Level
                  </Button>
                )}
              </div>

              {formData.escalationPolicy.map((level, index) => (
                <div key={index} className="escalation-level mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <Badge bg="primary">Level {index + 1}</Badge>
                    {formData.escalationPolicy.length > 1 && (
                      <Button
                        variant="link"
                        size="sm"
                        className="text-danger p-0"
                        onClick={() => removeEscalationLevel(index)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    )}
                  </div>

                  <Form.Group className="mb-2">
                    <Form.Label>Notify After (minutes) *</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      value={level.notifyAfterMinutes}
                      onChange={(e) => handleEscalationChange(index, 'notifyAfterMinutes', parseInt(e.target.value))}
                      isInvalid={!!validation[`escalation_${index}_minutes`]}
                    />
                    <Form.Control.Feedback type="invalid">
                      {validation[`escalation_${index}_minutes`]}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Label>To (Email Recipients) *</Form.Label>
                    <CreatableSelect
                      isMulti
                      options={userOptions}
                      value={level.emails.map(email => {
                        const existingOption = userOptions.find(u => u.value === email);
                        return existingOption || { value: email, label: email };
                      })}
                      onChange={(selected) => handleEscalationChange(index, 'emails', selected ? selected.map(s => s.value) : [])}
                      placeholder="Select or type email addresses..."
                      className={validation[`escalation_${index}_emails`] ? 'is-invalid' : ''}
                      formatCreateLabel={(inputValue) => `Add email: ${inputValue}`}
                      isValidNewOption={(inputValue) => isValidEmail(inputValue)}
                    />
                    {validation[`escalation_${index}_emails`] && (
                      <div className="invalid-feedback d-block">
                        {validation[`escalation_${index}_emails`]}
                      </div>
                    )}
                    <Form.Text className="text-muted">
                      Select from list or type email address and press Enter
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-0">
                    <Form.Label>CC</Form.Label>
                    <CreatableSelect
                      isMulti
                      options={userOptions}
                      value={(level.cc || []).map(email => {
                        const existingOption = userOptions.find(u => u.value === email);
                        return existingOption || { value: email, label: email };
                      })}
                      onChange={(selected) => handleEscalationChange(index, 'cc', selected ? selected.map(s => s.value) : [])}
                      placeholder="Select or type CC email addresses..."
                      className={validation[`escalation_${index}_cc`] ? 'is-invalid' : ''}
                      formatCreateLabel={(inputValue) => `Add email: ${inputValue}`}
                      isValidNewOption={(inputValue) => isValidEmail(inputValue)}
                    />
                    {validation[`escalation_${index}_cc`] && (
                      <div className="invalid-feedback d-block">
                        {validation[`escalation_${index}_cc`]}
                      </div>
                    )}
                    <Form.Text className="text-muted">
                      Select from list or type email address and press Enter
                    </Form.Text>
                  </Form.Group>
                </div>
              ))}
            </div>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? 'Saving...' : editingRule ? 'Update Alert Rule' : 'Create Alert Rule'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <SelectedMetersModal
        show={showSelectedMeters}
        onHide={() => setShowSelectedMeters(false)}
        meters={selectedMetersData}
      />
    </>
  );
});

export default AlertRuleForm;